import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import LayoutDefault from '../layouts/LayoutDefault';
import RouteSeo from '../../../seo/RouteSeo';
import { AUTHOR } from '../../../data/author';
import { MARKETING_PAGE_META } from '../../../data/marketingPageMeta';
import '../assets/scss/style.scss';

const PAPER = '#fcf9f7';
const PAPER_DEEP = '#f6efe7';

function getSocialLabel(url) {
    if (url.includes('x.com')) {
        return 'X';
    }

    if (url.includes('linkedin.com')) {
        return 'LinkedIn';
    }

    if (url.includes('instagram.com')) {
        return 'Instagram';
    }

    return url.replace(/^https?:\/\//, '').replace(/^www\./, '');
}

const COMPARISON_PAGES = MARKETING_PAGE_META
    .filter((page) => page.kind === 'comparison')
    .sort((left, right) => left.title.localeCompare(right.title));

const AuthorPage = () => {
    return (
        <>
            <RouteSeo
                title="About Uros | Founder of Luckynote"
                description="About Uros, founder of Luckynote, and how he compares note-taking, save-later, and capture tools from real weekly use."
                canonical={AUTHOR.url}
            />
            <LayoutDefault>
                <PageBackground>
                    <PageShell>
                        <HeroSection>
                            <PortraitWrap>
                                <Portrait
                                    src={require('../assets/images/founder-uros.jpg')}
                                    alt="Uros, founder of Luckynote"
                                />
                            </PortraitWrap>
                            <HeroContent>
                                <Eyebrow>Author</Eyebrow>
                                <HeroTitle>{AUTHOR.name}</HeroTitle>
                                <HeroRole>{AUTHOR.jobTitle}</HeroRole>
                                <HeroBio>{AUTHOR.bio}</HeroBio>
                                <SocialRow>
                                    {AUTHOR.sameAs.map((url) => (
                                        <SocialLink key={url} href={url} target="_blank" rel="noopener noreferrer">
                                            {getSocialLabel(url)}
                                        </SocialLink>
                                    ))}
                                </SocialRow>
                            </HeroContent>
                        </HeroSection>

                        <CardSection>
                            <SectionTitle>How I compare tools</SectionTitle>
                            <SectionBody>
                                I sign up for each tool, use it for real capture for at least a week, and then compare
                                how fast it is to save something, how easy it is to retrieve later, what it costs, and
                                which platforms it actually covers well.
                            </SectionBody>
                        </CardSection>

                        <CardSection>
                            <SectionTitle>Comparison pages</SectionTitle>
                            <LinksGrid>
                                {COMPARISON_PAGES.map((page) => (
                                    <CompareLink key={page.path} to={page.path}>
                                        {page.title}
                                    </CompareLink>
                                ))}
                            </LinksGrid>
                        </CardSection>
                    </PageShell>
                </PageBackground>
            </LayoutDefault>
        </>
    );
};

const PageBackground = styled.div`
    background:
        radial-gradient(circle at 14% 4%, rgba(255, 255, 255, 0.95), rgba(255, 255, 255, 0) 26rem),
        linear-gradient(180deg, ${PAPER} 0%, ${PAPER_DEEP} 56%, ${PAPER} 100%);
    min-height: 100vh;
`;

const PageShell = styled.div`
    max-width: 1120px;
    margin: 0 auto;
    padding: 96px 20px;
`;

const HeroSection = styled.section`
    display: grid;
    grid-template-columns: minmax(0, 280px) minmax(0, 1fr);
    gap: 32px;
    align-items: center;
    margin-bottom: 28px;

    @media (max-width: 820px) {
        grid-template-columns: 1fr;
    }
`;

const PortraitWrap = styled.div`
    border-radius: 22px;
    border: 1px solid rgba(20, 17, 15, 0.12);
    background: rgba(255, 255, 255, 0.62);
    padding: 18px;
    backdrop-filter: blur(12px);
`;

const Portrait = styled.img`
    width: 100%;
    display: block;
    border-radius: 16px;
    object-fit: cover;
`;

const HeroContent = styled.div`
    max-width: 720px;
`;

const Eyebrow = styled.p`
    margin: 0 0 14px;
    font-size: 0.82rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(20, 17, 15, 0.48);
`;

const HeroTitle = styled.h1`
    margin: 0;
    font-size: clamp(2.2rem, 4vw, 3.6rem);
    line-height: 1.05;
    letter-spacing: -0.03em;
    color: #14110f;
`;

const HeroRole = styled.p`
    margin: 14px 0 0;
    font-size: 1.05rem;
    font-weight: 600;
    color: rgba(20, 17, 15, 0.66);
`;

const HeroBio = styled.p`
    margin: 18px 0 0;
    font-size: 1.02rem;
    line-height: 1.8;
    color: rgba(20, 17, 15, 0.7);
`;

const SocialRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 22px;
`;

const SocialLink = styled.a`
    display: inline-flex;
    align-items: center;
    min-height: 40px;
    padding: 0 14px;
    border-radius: 999px;
    border: 1px solid rgba(20, 17, 15, 0.12);
    background: rgba(255, 255, 255, 0.72);
    && {
        color: #14110f;
    }
    font-size: 0.92rem;
    font-weight: 600;
    text-decoration: none;

    &&:hover {
        border-color: rgba(20, 17, 15, 0.22);
        color: #2b88d9;
    }
`;

const CardSection = styled.section`
    margin-top: 22px;
    padding: 28px 26px;
    border-radius: 18px;
    border: 1px solid rgba(20, 17, 15, 0.12);
    background: rgba(255, 255, 255, 0.58);
    backdrop-filter: blur(12px);
`;

const SectionTitle = styled.h2`
    margin: 0 0 14px;
    font-size: 1.2rem;
    color: #14110f;
`;

const SectionBody = styled.p`
    margin: 0;
    max-width: 760px;
    font-size: 1rem;
    line-height: 1.75;
    color: rgba(20, 17, 15, 0.68);
`;

const LinksGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 14px;
`;

const CompareLink = styled(Link)`
    display: block;
    padding: 16px 18px;
    border-radius: 14px;
    border: 1px solid rgba(20, 17, 15, 0.12);
    background: rgba(252, 249, 247, 0.92);
    && {
        color: #14110f;
    }
    font-size: 0.96rem;
    font-weight: 600;
    line-height: 1.5;
    text-decoration: none;
    transition: border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;

    &&:hover {
        border-color: rgba(20, 17, 15, 0.22);
        box-shadow: 0 16px 36px rgba(59, 46, 35, 0.12);
        transform: translateY(-2px);
        color: #2b88d9;
    }
`;

export default AuthorPage;
